import {
  type MouseEvent,
  type RefObject,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { InputLabel } from "@mui/material";
import Popover from "@mui/material/Popover";
import cx from "classnames";
import { useSize } from "src/hooks/useSize";
import { DROPDOWN_SURFACE } from "src/theme/dropdownSurface";
import mergeDeep from "src/utils/object/deepMerge";
import { OutlinedContainer } from "./components/OutlinedContainer/OutlinedContainer";
import { OutlinedContainerEndAdornment } from "./components/OutlinedContainerEndAdornment/OutlinedContainerEndAdornment";
import {
  OFFSET,
  POPOVER_ANCHOR_ORIGIN,
  POPOVER_DEFAULT_SLOT_PROPS,
  POPOVER_TRANSFORM_ORIGIN,
} from "./SelectPopover.constants";
import { SelectPopoverWrapper } from "./SelectPopover.styled";
import type { SelectPopoverProps } from "./SelectPopover.types";

const SelectPopover = ({
  ref,
  children,
  className,
  label,
  legend,
  slotProps,
  size = "medium",
  minSize,
  popoverAutoFit,
  popoverMinAutoFit,
  phCaptureAction = "open_select",
  onClick,
  ...props
}: SelectPopoverProps) => {
  const [anchorEl, setAnchorEl] = useState<HTMLDivElement | null>(null);

  const localRef = useRef<HTMLDivElement | null>(null);
  ref ||= localRef; // use supplied `ref` if available
  const containerSize = useSize(ref as RefObject<HTMLDivElement | null>);
  const containerWidth = (containerSize as DOMRect).width;
  const containerRect = ref?.current?.getBoundingClientRect?.();
  const containerRightPos = window.innerWidth - (containerRect?.right || 0);
  const isPositionedRight =
    slotProps?.Popover?.anchorOrigin?.horizontal === "right" &&
    slotProps?.Popover?.transformOrigin?.horizontal === "right";

  const onClickHandler = (e: MouseEvent<HTMLDivElement>) => {
    onClick?.(e, ref);
    children && setAnchorEl(e.currentTarget);
  };

  const handleClose = useCallback(() => {
    setAnchorEl(null);
    props.onClose?.();
  }, [props.onClose]);

  const popoverSlotProps = useMemo(
    () =>
      mergeDeep(
        {
          slotProps: {
            paper: {
              sx: {
                ...DROPDOWN_SURFACE,
                ...(isPositionedRight && {
                  right: containerRightPos,
                  left: "auto !important",
                }),
                minWidth: popoverMinAutoFit ? containerWidth : undefined,
                width: popoverAutoFit ? containerWidth : undefined,
              },
            },
            ...POPOVER_DEFAULT_SLOT_PROPS,
          },
        },
        slotProps?.Popover || {}
      ),
    [
      containerWidth,
      popoverMinAutoFit,
      popoverAutoFit,
      slotProps?.Popover,
      isPositionedRight,
      containerRightPos,
    ]
  );

  useEffect(() => {
    if (props.open) {
      setAnchorEl(ref.current as HTMLDivElement);
    } else if (props.open === false) {
      handleClose();
    }
  }, [props.open]);

  const open = Boolean(anchorEl);

  if (size === "fit" && !minSize) {
    minSize = "medium";
  }

  return (
    <>
      <SelectPopoverWrapper
        ref={ref}
        size={size}
        minSize={minSize}
        data-ph-capture-attribute-action={phCaptureAction}
        onClick={onClickHandler}
        className={cx(
          "group flex select-none items-center cursor-pointer",
          {
            "border-primary text-primary ": open,
          },
          className
        )}
        {...props}
      >
        {legend && <InputLabel shrink>{legend}</InputLabel>}

        <OutlinedContainer
          focused={open}
          fullWidth
          endAdornment={
            <OutlinedContainerEndAdornment
              open={open}
              clearable={slotProps?.OutlinedContainer?.clearable}
              onClear={(e: MouseEvent<HTMLButtonElement>) => {
                e.stopPropagation();
                slotProps?.OutlinedContainer?.onClear?.();
              }}
              className="-mr-2"
            />
          }
          label={legend}
          notched
          {...slotProps?.OutlinedContainer}
        >
          {typeof label === "function" ? label(open) : label}
        </OutlinedContainer>
      </SelectPopoverWrapper>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={POPOVER_ANCHOR_ORIGIN}
        transformOrigin={POPOVER_TRANSFORM_ORIGIN}
        {...popoverSlotProps}
        sx={{ margin: OFFSET, ...popoverSlotProps?.sx }}
      >
        {typeof children === "function"
          ? children({ width: containerWidth })
          : children}
      </Popover>
    </>
  );
};

export default SelectPopover;
